import dayjs from 'dayjs'
import * as Yup from 'yup'
import i18n from '../../../i18n'
import { activityResponseFields } from '../../response/activity'
import { activityFields } from './fields'

const createActivityDateValidationSchema = () =>
    Yup.object({
        [activityFields.DATE_FROM]: Yup.date(),
        [activityFields.TIME_FROM]: Yup.string(),
        [activityFields.DATE_TO]: Yup.date().min(Yup.ref(activityFields.DATE_FROM), i18n.t('activities.dateValidation.dateTo')),
        [activityFields.TIME_TO]: Yup.string().test({
            test: function (value) {
                const timeFrom = this.parent[activityFields.TIME_FROM]

                if (!timeFrom || !value) {
                    return true
                }

                const dateFrom = this.parent[activityFields.DATE_FROM]
                const dateTo = this.parent[activityFields.DATE_TO]

                // Only validate time if dateFrom and dateTo are equal
                if (dayjs(dateFrom).isSame(dayjs(dateTo))) {
                    const [hoursTo, minutesTo] = value.split(':')
                    const [hoursFrom, minutesFrom] = timeFrom.split(':')
                    const timeFromAsMinutes = parseInt(hoursFrom) * 60 + parseInt(minutesFrom)
                    const timeToAsMinutes = parseInt(hoursTo) * 60 + parseInt(minutesTo)
                    return timeToAsMinutes > timeFromAsMinutes
                }

                return true
            },
            message: i18n.t('activities.dateValidation.timeTo'),
        }),
    })

const createActivityReminderGroupValidationSchema = () =>
    Yup.array().of(
        Yup.object().shape({
            [activityFields.REMINDER_CHANNEL]: Yup.string()
                .nullable()
                .test('channel-required', i18n.t('validation.message'), function (value) {
                    return !this.parent[activityFields.REMINDER_MINUTES_BEFORE] || !!value
                }),
            [activityFields.REMINDER_MINUTES_BEFORE]: Yup.string()
                .nullable()
                .test('minutes-required', i18n.t('validation.message'), function (value) {
                    return !this.parent[activityFields.REMINDER_CHANNEL] || !!value
                }),
        })
    )

export const activityEventValidaton = (data, agentId) => {
    const initialValues = {
        [activityFields.ID]: data?.id ?? '',
        [activityFields.TITLE]: data?.title ?? '',
        [activityFields.ACTIVITY_TYPE_ID]: data?.[activityResponseFields.ACTIVITY_TYPE]?.id ?? '',
        [activityFields.DATE_FROM]: data?.[activityResponseFields.DATE_FROM] ?? '',
        [activityFields.TIME_FROM]: data?.[activityResponseFields.TIME_FROM] ?? '',
        [activityFields.DATE_TO]: data?.[activityResponseFields.DATE_TO] ?? '',
        [activityFields.TIME_TO]: data?.[activityResponseFields.TIME_TO] ?? '',
        [activityFields.DURATION_DAY]: data?.[activityResponseFields.DURATION_DAY] ?? false,
        [activityFields.COMPLETED]: data?.[activityResponseFields.COMPLETED] ?? false,
        [activityFields.ACTIVITY_LOCATION]: data?.location ?? '',
        [activityFields.CONTACT_ID]: data?.[activityResponseFields.CONTACT]?.id ?? '',
        [activityFields.CONTACT_COMPANY_COLLECTION]:
            data?.contactCompany?.map((x) => ({
                [activityFields.CONTACT_ID]: x?.id?.toString(),
            })) ?? [],
        [activityFields.ACTIVITY_AGENT_GROUP]: data?.[activityResponseFields.ACTIVITY_AGENT]?.map((item) => ({
            [activityFields.AGENT_ID]: item?.[activityResponseFields.AGENT]?.id,
            [activityFields.NOTIFICATION_SEND]: item?.[activityResponseFields.NOTIFICATION_SEND] === 1,
        })) ?? [
            {
                [activityFields.AGENT_ID]: agentId ?? 1,
                [activityFields.NOTIFICATION_SEND]: false,
            },
        ],
        [activityFields.ACTIVITY_REMINDER_GROUP]:
            data?.[activityResponseFields.ACTIVITY_REMINDER]?.map((item) => ({
                [activityFields.REMINDER_CHANNEL]: item?.[activityResponseFields.REMINDER_CHANNEL] ?? null,
                [activityFields.REMINDER_MINUTES_BEFORE]: item?.[activityResponseFields.REMINDER_MINUTES_BEFORE]?.toString() ?? null,
            })) ?? [],
        [activityFields.PROPERTY_ID]: data?.[activityResponseFields.PROPERTY]?.map((item) => item.id.toString()) ?? [],
        [activityFields.PROJECT_ID]: data?.[activityResponseFields.PROJECT]?.map((item) => item.id.toString()) ?? [],
        [activityFields.NOTES]: data?.notes ?? '',
    }

    const validationSchema = {
        [activityFields.TITLE]: Yup.string().required(i18n.t('modal.activity.error.activityTitle')),
        [activityFields.ACTIVITY_TYPE_ID]: Yup.number().required(i18n.t('modal.activity.error.activityType')),
        [activityFields.DURATION_DAY]: Yup.bool(),
        [activityFields.NOTES]: Yup.string(),
        [activityFields.ACTIVITY_AGENT_GROUP]: Yup.array().of(
            Yup.object().shape({
                [activityFields.AGENT_ID]: Yup.number().required(i18n.t('validation.message')),
                [activityFields.NOTIFICATION_SEND]: Yup.bool(),
            })
        ),
        [activityFields.ACTIVITY_REMINDER_GROUP]: createActivityReminderGroupValidationSchema(),
    }

    const validationObject = {
        initialValues: initialValues,
        validationSchema: Yup.object(validationSchema).concat(createActivityDateValidationSchema()),
    }

    return validationObject
}

export const activityTaskValidaton = (data, agentId) => {
    const initialValues = {
        [activityFields.ID]: data?.id ?? '',
        [activityFields.TITLE]: data?.title ?? '',
        [activityFields.ACTIVITY_TYPE_ID]: data?.[activityResponseFields.ACTIVITY_TYPE]?.id ?? '',
        [activityFields.DATE_FROM]: data?.[activityResponseFields.DATE_FROM] ?? '',
        [activityFields.TIME_FROM]: data?.[activityResponseFields.TIME_FROM] ?? '',
        [activityFields.DATE_TO]: data?.[activityResponseFields.DATE_TO] ?? '',
        [activityFields.TIME_TO]: data?.[activityResponseFields.TIME_TO] ?? '',
        [activityFields.DURATION_DAY]: data?.[activityResponseFields.DURATION_DAY] ?? false,
        [activityFields.COMPLETED]: data?.[activityResponseFields.COMPLETED] ?? false,
        [activityFields.CONTACT_ID]: data?.[activityResponseFields.CONTACT]?.id ?? '',
        [activityFields.CONTACT_COMPANY_COLLECTION]:
            data?.contactCompany?.map((x) => ({
                [activityFields.CONTACT_ID]: x?.id?.toString(),
            })) ?? [],
        [activityFields.ACTIVITY_AGENT_GROUP]: data?.[activityResponseFields.ACTIVITY_AGENT]?.map((item) => ({
            [activityFields.AGENT_ID]: item?.[activityResponseFields.AGENT]?.id,
            [activityFields.NOTIFICATION_SEND]: !!item?.[activityResponseFields.NOTIFICATION_SEND],
        })) ?? [
            {
                [activityFields.AGENT_ID]: agentId ?? 1,
                [activityFields.NOTIFICATION_SEND]: false,
            },
        ],
        [activityFields.ACTIVITY_REMINDER_GROUP]:
            data?.[activityResponseFields.ACTIVITY_REMINDER]?.map((item) => ({
                [activityFields.REMINDER_CHANNEL]: item?.[activityResponseFields.REMINDER_CHANNEL] ?? null,
                [activityFields.REMINDER_MINUTES_BEFORE]: item?.[activityResponseFields.REMINDER_MINUTES_BEFORE]?.toString() ?? null,
            })) ?? [],
        [activityFields.PROPERTY_ID]: data?.[activityResponseFields.PROPERTY]?.map((item) => item.id.toString()) ?? [],
        [activityFields.PROJECT_ID]: data?.[activityResponseFields.PROJECT]?.map((item) => item.id.toString()) ?? [],
        [activityFields.NOTES]: data?.notes ?? '',
    }
    const validationSchema = {
        [activityFields.TITLE]: Yup.string().required(i18n.t('modal.activity.error.activityTitle')),
        [activityFields.ACTIVITY_TYPE_ID]: Yup.number().required(i18n.t('modal.activity.error.activityType')),
        [activityFields.DURATION_DAY]: Yup.bool(),
        [activityFields.CONTACT_ID]: Yup.number(),

        [activityFields.ACTIVITY_AGENT_GROUP]: Yup.array().of(
            Yup.object().shape({
                [activityFields.AGENT_ID]: Yup.number(),
                [activityFields.NOTIFICATION_SEND]: Yup.bool(),
            })
        ),
        [activityFields.ACTIVITY_REMINDER_GROUP]: createActivityReminderGroupValidationSchema(),
        [activityFields.NOTES]: Yup.string(),
    }

    const validationObject = {
        initialValues: initialValues,
        validationSchema: Yup.object(validationSchema).concat(createActivityDateValidationSchema()),
    }

    return validationObject
}

export const activityReminderValidaton = (data, agentId) => {
    const initialValues = {
        [activityFields.ID]: data?.id ?? '',
        [activityFields.TITLE]: data?.title ?? '',
        [activityFields.ACTIVITY_TYPE_ID]: data?.[activityResponseFields.ACTIVITY_TYPE]?.id ?? '',
        [activityFields.DATE_FROM]: data?.[activityResponseFields.DATE_FROM] ?? '',
        [activityFields.TIME_FROM]: data?.[activityResponseFields.TIME_FROM] ?? '',
        [activityFields.DATE_TO]: data?.[activityResponseFields.DATE_TO] ?? '',
        [activityFields.TIME_TO]: data?.[activityResponseFields.TIME_TO] ?? '',
        [activityFields.DURATION_DAY]: data?.[activityResponseFields.DURATION_DAY] ?? false,
        [activityFields.COMPLETED]: data?.[activityResponseFields.COMPLETED] ?? false,
        [activityFields.CONTACT_ID]: data?.[activityResponseFields.CONTACT]?.id ?? '',
        [activityFields.CONTACT_COMPANY_COLLECTION]:
            data?.contactCompany?.map((x) => ({
                [activityFields.CONTACT_ID]: x?.id?.toString(),
            })) ?? [],
        [activityFields.ACTIVITY_AGENT_GROUP]: data?.[activityResponseFields.ACTIVITY_AGENT]?.map((item) => ({
            [activityFields.AGENT_ID]: item?.[activityResponseFields.AGENT]?.id,
            [activityFields.NOTIFICATION_SEND]: item?.[activityResponseFields.NOTIFICATION_SEND] === 1,
        })) ?? [
            {
                [activityFields.AGENT_ID]: agentId ?? 1,
                [activityFields.NOTIFICATION_SEND]: false,
            },
        ],
        [activityFields.ACTIVITY_REMINDER_GROUP]: data?.activityReminderGroup ?? [],
        [activityFields.PROPERTY_ID]: data?.[activityResponseFields.PROPERTY]?.map((item) => item.id.toString()) ?? [],
        [activityFields.PROJECT_ID]: data?.[activityResponseFields.PROJECT]?.map((item) => item.id.toString()) ?? [],
        [activityFields.NOTES]: data?.notes ?? '',
    }
    const validationSchema = {
        [activityFields.TITLE]: Yup.string().required(i18n.t('modal.activity.error.activityTitle')),
        [activityFields.ACTIVITY_TYPE_ID]: Yup.number().required(i18n.t('modal.activity.error.activityType')),
        [activityFields.DURATION_DAY]: Yup.bool(),
        [activityFields.CONTACT_ID]: Yup.number(),

        [activityFields.ACTIVITY_AGENT_GROUP]: Yup.array().of(
            Yup.object().shape({
                [activityFields.AGENT_ID]: Yup.number(),
                [activityFields.NOTIFICATION_SEND]: Yup.bool(),
            })
        ),
        [activityFields.ACTIVITY_REMINDER_GROUP]: createActivityReminderGroupValidationSchema(),

        [activityFields.NOTES]: Yup.string(),
    }

    const validationObject = {
        initialValues: initialValues,
        validationSchema: Yup.object(validationSchema).concat(createActivityDateValidationSchema()),
    }

    return validationObject
}
